/**
 * Sync a user's approved group IDs to their Firestore user document.
 * Used so security rules can check venue booking access for private venues
 * (user.approvedGroupIds.hasAny(venue.allowedGroupIds)) without querying groupMembers.
 *
 * Also syncs a group's memberIds and membershipIds from groupMembers so groups
 * are managed well (denormalized list of members and membership doc ids on the group).
 */

import { doc, updateDoc, getDocs, query, collection, where } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Update users/{userId}.approvedGroupIds from groupMembers (approved only).
 * Call after join/leave group or approval change.
 * When called by a group admin (e.g. from GroupManage after approve/revoke), pass contextGroupId
 * so Firestore rules can allow the update (group admin can only write approvedGroupIds + context).
 * @param {string} userId
 * @param {string} [contextGroupId] - groupId when caller is a group admin (enables rule to allow update)
 */
export async function syncUserApprovedGroupIds(userId, contextGroupId) {
  if (!userId) return;
  try {
    const membersSnap = await getDocs(
      query(collection(db, 'groupMembers'), where('userId', '==', userId))
    );
    const approvedGroupIds = membersSnap.docs
      .filter((d) => d.data().approved === true)
      .map((d) => d.data().groupId)
      .filter(Boolean);
    const unique = [...new Set(approvedGroupIds)];
    const payload = contextGroupId
      ? { approvedGroupIds: unique, approvalContextGroupId: contextGroupId }
      : { approvedGroupIds: unique };
    await updateDoc(doc(db, 'users', userId), payload);
  } catch (err) {
    console.warn('[userGroupSync] Failed to sync approvedGroupIds:', err?.message);
  }
}

/**
 * Update groups/{groupId}.memberIds and .membershipIds from groupMembers.
 * Call after join/leave group so the group doc stays in sync.
 * @param {string} groupId
 */
export async function syncGroupMemberIds(groupId) {
  if (!groupId) return;
  try {
    const membersSnap = await getDocs(
      query(collection(db, 'groupMembers'), where('groupId', '==', groupId))
    );
    const memberIds = [];
    const membershipIds = [];
    membersSnap.docs.forEach((d) => {
      const data = d.data();
      if (data.userId) memberIds.push(data.userId);
      membershipIds.push(d.id);
    });
    const groupRef = doc(db, 'groups', groupId);
    await updateDoc(groupRef, {
      memberIds: [...new Set(memberIds)],
      membershipIds: [...new Set(membershipIds)],
    });
  } catch (err) {
    console.warn('[userGroupSync] Failed to sync group memberIds:', err?.message);
  }
}
